import {
    Button, Flex, HStack, InfoBox, Input, Modal, NumberInput, Select, Text, Textarea, VStack
} from 'components/ui';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { createMaskingRule, CreateMaskingRuleDto, MaskingRuleScope } from 'services/maskingService';
import { serverService } from 'services/serverService';
import { ToolConfig, toolService } from 'services/toolService';

interface CreateMaskingRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Server {
  id: string;
  name: string;
}

const CreateMaskingRuleModal = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateMaskingRuleModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [servers, setServers] = useState<Server[]>([]);
  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [formData, setFormData] = useState<CreateMaskingRuleDto>({
    name: '',
    description: '',
    fieldPath: '',
    keepFirstChars: 2,
    keepLastChars: 2,
    maskingSymbol: '*',
    priority: 0,
    scope: MaskingRuleScope.Global,
  });

  useEffect(() => {
    if (isOpen) {
      loadServers();
    }
  }, [isOpen]);

  const loadServers = async () => {
    try {
      const data = await serverService.getAllServers();
      setServers(data.map(s => ({ id: s.id!, name: s.name })));
    } catch (error) {
      console.error('Error loading servers:', error);
    }
  };

  const loadTools = async (serverId: string) => {
    if (!serverId) {
      setTools([]);
      return;
    }
    setLoadingTools(true);
    try {
      const data = await toolService.getServerTools(serverId);
      setTools(data);
    } catch (error) {
      console.error('Error loading tools:', error);
      setTools([]);
    } finally {
      setLoadingTools(false);
    }
  };

  useEffect(() => {
    if (formData.serverId) {
      loadTools(formData.serverId);
    } else {
      setTools([]);
      setFormData(prev => ({ ...prev, toolName: undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.serverId]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.fieldPath) {
      setError(t('app.security.createMaskingRuleModal.nameRequired'));
      return;
    }

    if (
      (formData.scope === MaskingRuleScope.Server || formData.scope === MaskingRuleScope.ServerTool) &&
      !formData.serverId
    ) {
      setError(t('app.security.createMaskingRuleModal.serverRequired'));
      return;
    }

    if (formData.scope === MaskingRuleScope.ServerTool && !formData.toolName) {
      setError(t('app.security.createMaskingRuleModal.toolRequired'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      await createMaskingRule(formData);
      setSuccess(t('app.security.createMaskingRuleModal.success'));
      setTimeout(() => {
        setSuccess('');
        onSuccess();
        onClose();
        // Reset form
        setFormData({
          name: '',
          description: '',
          fieldPath: '',
          keepFirstChars: 2,
          keepLastChars: 2,
          maskingSymbol: '*',
          priority: 0,
          scope: MaskingRuleScope.Global,
        });
      }, 1500);
    } catch (err: any) {
      setError(err.message || t('app.security.createMaskingRuleModal.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'var(--modal-header-text)'
          }}>
            {t('app.security.createMaskingRuleModal.title')}
          </h3>
          <Modal.CloseTrigger asChild>
            <button style={{
              color: 'var(--modal-close-icon)',
              transition: 'color 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--modal-close-icon-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--modal-close-icon)'}>
              <MdClose style={{ height: '1.5rem', width: '1.5rem' }} />
            </button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <InfoBox variant="error" mb={4}>
            <HStack justify="space-between">
              <Text color="red.700">{error}</Text>
              <Button onClick={() => setError('')} size="sm" variant="ghost">
                {t('app.security.createMaskingRuleModal.dismiss')}
              </Button>
            </HStack>
          </InfoBox>
        )}

        {success && (
          <InfoBox variant="success" mb={4}>
            <Text color="green.700">{success}</Text>
          </InfoBox>
        )}

        <VStack gap={4}>
          <Input
            label={t('app.security.createMaskingRuleModal.nameLabel')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('app.security.createMaskingRuleModal.namePlaceholder')}
            required
          />

          <Textarea
            label={t('app.security.createMaskingRuleModal.descriptionLabel')}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder={t('app.security.createMaskingRuleModal.descriptionPlaceholder')}
            rows={2}
          />

          <Input
            label={t('app.security.createMaskingRuleModal.fieldPathLabel')}
            value={formData.fieldPath}
            onChange={(e) =>
              setFormData({ ...formData, fieldPath: e.target.value })
            }
            placeholder={t('app.security.createMaskingRuleModal.fieldPathPlaceholder')}
            helperText={t('app.security.createMaskingRuleModal.fieldPathHelper')}
            required
          />

          <Select
            label={t('app.security.createMaskingRuleModal.scopeLabel')}
            value={[formData.scope.toString()]}
            onValueChange={(details: any) =>
              setFormData({
                ...formData,
                scope: parseInt(details.value[0]) as MaskingRuleScope,
              })
            }
            items={[
              { label: t('app.security.createMaskingRuleModal.scopeGlobal'), value: MaskingRuleScope.Global.toString() },
              { label: t('app.security.createMaskingRuleModal.scopeServer'), value: MaskingRuleScope.Server.toString() },
              { label: t('app.security.createMaskingRuleModal.scopeServerTool'), value: MaskingRuleScope.ServerTool.toString() },
            ]}
          />

          {(formData.scope === MaskingRuleScope.Server || formData.scope === MaskingRuleScope.ServerTool) && (
            <Select
              label={t('app.security.createMaskingRuleModal.serverLabel')}
              value={formData.serverId ? [formData.serverId] : []}
              onValueChange={(details: any) =>
                setFormData({ ...formData, serverId: details.value[0] })
              }
              items={[
                { label: t('app.security.createMaskingRuleModal.serverPlaceholder'), value: '' },
                ...servers.map((server) => ({
                  label: server.name,
                  value: server.id,
                })),
              ]}
            />
          )}

          {formData.scope === MaskingRuleScope.ServerTool && (
            <Select
              label={t('app.security.createMaskingRuleModal.toolLabel')}
              value={formData.toolName ? [formData.toolName] : []}
              onValueChange={(details: any) =>
                setFormData({ ...formData, toolName: details.value[0] })
              }
              items={[
                { label: t('app.security.createMaskingRuleModal.toolPlaceholder'), value: '' },
                ...tools.map((tool) => ({
                  label: tool.name,
                  value: tool.name,
                })),
              ]}
              disabled={!formData.serverId || loadingTools}
            />
          )}

          <NumberInput
            label={t('app.security.createMaskingRuleModal.keepFirstLabel')}
            value={formData.keepFirstChars}
            onValueChange={(val: number) =>
              setFormData({ ...formData, keepFirstChars: val })
            }
            min={0}
            max={50}
            required
          />

          <NumberInput
            label={t('app.security.createMaskingRuleModal.keepLastLabel')}
            value={formData.keepLastChars}
            onValueChange={(val: number) =>
              setFormData({ ...formData, keepLastChars: val })
            }
            min={0}
            max={50}
            required
          />

          <Input
            label={t('app.security.createMaskingRuleModal.maskingSymbolLabel')}
            value={formData.maskingSymbol}
            onChange={(e) =>
              setFormData({ ...formData, maskingSymbol: e.target.value })
            }
            maxLength={1}
            placeholder={t('app.security.createMaskingRuleModal.maskingSymbolPlaceholder')}
            helperText={t('app.security.createMaskingRuleModal.maskingSymbolHelper')}
            required
          />

          <NumberInput
            label={t('app.security.createMaskingRuleModal.priorityLabel')}
            value={formData.priority}
            onValueChange={(val: number) => setFormData({ ...formData, priority: val })}
            min={0}
            max={100}
            helperText={t('app.security.createMaskingRuleModal.priorityHelper')}
          />
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <Flex gap={3}>
          <Button variant="ghost" onClick={onClose}>
            {t('app.security.createMaskingRuleModal.cancel')}
          </Button>
          <Button colorPalette="blue" onClick={handleSubmit} loading={loading}>
            {t('app.security.createMaskingRuleModal.create')}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateMaskingRuleModal;
